import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  Copy, 
  Check, 
  Link as LinkIcon, 
  Trash2, 
  Loader2,
  Eye,
  ExternalLink
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShareModal = ({ open, onClose, conversationId, conversationTitle }) => {
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open && conversationId) {
      setLoading(true);
      setShareInfo(null);
      axios.get(`${API}/shares/conversations/${conversationId}`)
        .then(response => {
          setShareInfo(response.data);
        })
        .catch(err => {
          console.error('Failed to fetch share info:', err);
          setShareInfo({ shared: false });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, conversationId]);

  const createShareLink = async () => {
    try {
      setCreating(true);
      const response = await axios.post(`${API}/shares/conversations/${conversationId}`);
      setShareInfo({
        shared: true,
        ...response.data
      });
      toast.success('Share link created!');
    } catch (err) {
      toast.error('Failed to create share link');
    } finally {
      setCreating(false);
    }
  };

  const revokeShareLink = async () => {
    try {
      setRevoking(true);
      await axios.delete(`${API}/shares/conversations/${conversationId}`);
      setShareInfo({ shared: false });
      toast.success('Share link revoked');
    } catch (err) {
      toast.error('Failed to revoke share link');
    } finally {
      setRevoking(false);
    }
  };

  const copyLink = async () => {
    const fullUrl = `${window.location.origin}/shared/${shareInfo.share_id}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const getFullShareUrl = () => {
    if (!shareInfo?.share_id) return '';
    return `${window.location.origin}/shared/${shareInfo.share_id}`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LinkIcon className="h-5 w-5" />
            Share Conversation
          </DialogTitle>
          <DialogDescription>
            {conversationTitle ? `"${conversationTitle}"` : 'Create a shareable link for this conversation'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : shareInfo?.shared ? (
            <>
              {/* Share link display */}
              <div className="space-y-3">
                <label className="text-sm font-medium">Share Link</label>
                <div className="flex gap-2">
                  <Input 
                    readOnly 
                    value={getFullShareUrl()}
                    className="flex-1 text-sm"
                    data-testid="share-link-input"
                  />
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={copyLink}
                    data-testid="copy-share-link-btn"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Stats */}
              {shareInfo.view_count !== undefined && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Eye className="h-4 w-4" />
                  <span>{shareInfo.view_count} view{shareInfo.view_count !== 1 ? 's' : ''}</span>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => window.open(getFullShareUrl(), '_blank')}
                  data-testid="preview-share-btn"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Preview
                </Button>
                <Button
                  variant="destructive"
                  onClick={revokeShareLink}
                  disabled={revoking}
                  data-testid="revoke-share-btn"
                >
                  {revoking ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Revoke
                    </>
                  )}
                </Button>
              </div>

              <p className="text-xs text-muted-foreground">
                Anyone with this link can view this conversation. Revoke the link to stop sharing.
              </p>
            </>
          ) : (
            <>
              {/* Not shared yet */}
              <div className="text-center py-4">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                  <LinkIcon className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Create a link to share this conversation with anyone. They'll be able to view the messages without signing in.
                </p>
                <Button 
                  onClick={createShareLink} 
                  disabled={creating}
                  className="w-full"
                  data-testid="create-share-link-btn"
                >
                  {creating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <LinkIcon className="h-4 w-4 mr-2" />
                      Create Share Link
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareModal;
